import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { useCurrency } from "../context/CurrencyContext";
import { formatPrice } from "../utils/helpers";
import api from "../utils/api";

const PayPalReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { exchangeRates, selectedCurrency } = useCurrency();
  const [processing, setProcessing] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const capturePayment = async () => {
      const token = searchParams.get("token");
      const orderId = searchParams.get("orderId");

      if (!token || !orderId) {
        setError("Missing payment information");
        setProcessing(false);
        return;
      }

      try {
        const response = await api.post(`/orders/${orderId}/paypal/capture`, { paypalOrderId: token });

        if (response.data.success) {
          setOrder(response.data.order);
          toast.success("Payment successful!");
        } else {
          setError("Payment capture failed");
        }
      } catch (err) {
        console.error("Error capturing payment:", err);
        setError(err.response?.data?.error || "Payment failed");
        toast.error("Payment processing failed");
      } finally {
        setProcessing(false);
      }
    };

    capturePayment();
  }, [searchParams]);

  if (processing) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-white text-lg">Processing your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="bg-gradient-to-r from-black via-gray-900 to-black p-8 rounded-lg border border-red-500 max-w-md">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">✕</div>
            <h1 className="text-2xl font-bold text-white mb-4">Payment Failed</h1>
            <p className="text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/checkout')}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-black via-gray-900 to-black p-8 rounded-lg border border-gold"
        >
          <div className="text-center mb-8">
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h1 className="heading-metallic text-3xl mb-2">Payment Successful!</h1>
            <p className="text-gray-400">Thank you for your order</p>
          </div>

          <div className="border-t border-gray-700 pt-6">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-400">Order Number</p>
                <p className="text-lg font-bold text-white">{order.order_number}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Amount</p>
                <p className="text-lg font-bold text-gold">{formatPrice(order.total, selectedCurrency, exchangeRates)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Payment Method</p>
                <p className="text-lg text-white capitalize">{order.payment_method}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Order Status</p>
                <p className="text-lg text-white capitalize">{order.status}</p>
              </div>
            </div>

            <div className="bg-black p-4 rounded-lg mb-6">
              <h3 className="heading-metallic-soft text-lg mb-3">Order Items</h3>
              <div className="space-y-2">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-300">
                      {item.name} × {item.quantity}
                    </span>
                    <span className="text-white">
                      {formatPrice(parseFloat(item.price) * item.quantity, selectedCurrency, exchangeRates)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {selectedCurrency !== "EUR" && <p className="mb-6 text-sm text-gray-400">Your payment was captured in EUR. Displayed totals reflect your selected storefront currency.</p>}

            <div className="flex gap-4">
              <button
                onClick={() => navigate('/orders')}
                className="btn-primary flex-1"
              >
                View All Orders
              </button>
              <button
                onClick={() => navigate('/products')}
                className="btn-secondary flex-1"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PayPalReturn;
